import React, { useRef, useState } from "react";
import { api, apiUpload } from "../api";
import { CHANNEL_LABELS } from "./ui";

// Three capture modes, one form. Manual entry is always person-first — the
// company enters the pipeline at 'new' as a side effect of the lead being
// created (automation rule 1), which POST /api/contacts handles server-side.
const TABS = [["url", "Paste LinkedIn URL"], ["screenshot", "Drop screenshot"], ["manual", "Manual"]];

const EMPTY = {
  first_name: "", last_name: "", job_title: "", company_name: "", email: "",
  linkedin_url: "", lead_channel: "linkedin_dm",
};

// Enrichment/extraction misses are never blocking — every one of these lines
// ends in "fill the fields in manually".
const LOOKUP_MESSAGE = {
  no_key: "Apollo isn’t configured — fill the fields in manually. The URL stays on the lead.",
  no_match: "No Apollo match — fill the fields in manually. The URL stays on the lead.",
  no_url: "That doesn’t look like a LinkedIn profile URL.",
  error: "Apollo lookup failed — fill the fields in manually. The URL stays on the lead.",
};
const EXTRACT_MESSAGE = {
  no_key: "Extraction isn’t configured — fill the fields in manually. The screenshot is still saved.",
  unsupported_type: "That file type can’t be read — use a PNG, JPEG, GIF or WebP screenshot.",
  error: "Couldn’t read that screenshot — fill the fields in manually. It is still saved.",
};

// The DM lands on the timeline as the activity its channel implies; a
// screenshot dropped against any other channel is kept as a plain note so it
// is never silently discarded.
const DM_ACTIVITY = { whatsapp_dm: "whatsapp_message", linkedin_dm: "linkedin_message" };

export default function NewLeadModal({ onClose, onSaved }) {
  const [tab, setTab] = useState("url");
  const [form, setForm] = useState(EMPTY);
  const [lookup, setLookup] = useState({ state: "idle" }); // idle | looking | found | miss
  const [shot, setShot] = useState(null); // { file, url, state, reason }
  const [dmMessage, setDmMessage] = useState("");
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileInput = useRef(null);
  const lastLookedUp = useRef("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const merge = (fields) => setForm((f) => ({
    ...f,
    // Never overwrite something already typed in — enrichment prefills, it
    // doesn't take over.
    ...Object.fromEntries(Object.entries(fields).filter(([k, v]) => v && !f[k])),
  }));

  // --- LinkedIn URL mode

  async function lookUp() {
    const url = form.linkedin_url.trim();
    if (!url || url === lastLookedUp.current) return;
    lastLookedUp.current = url;
    setLookup({ state: "looking" });
    try {
      const r = await api("/enrich/linkedin", { method: "POST", body: JSON.stringify({ linkedin_url: url }) });
      if (!r.found) return setLookup({ state: "miss", reason: r.reason || "no_match" });
      merge({
        first_name: r.first_name, last_name: r.last_name, job_title: r.job_title,
        company_name: r.company_name, email: r.email,
      });
      setLookup({ state: "found" });
    } catch (e) {
      setLookup({ state: "miss", reason: "error" });
    }
  }

  // --- Screenshot mode

  async function takeFile(file) {
    if (!file) return;
    setShot({ file, url: URL.createObjectURL(file), state: "reading" });
    try {
      const r = await apiUpload("/enrich/screenshot", file);
      if (!r.found) return setShot((s) => ({ ...s, state: "miss", reason: r.reason || "error" }));
      merge({
        first_name: r.first_name, last_name: r.last_name, job_title: r.job_title,
        company_name: r.company_name, email: r.email, linkedin_url: r.linkedin_url,
      });
      if (r.lead_channel) setForm((f) => ({ ...f, lead_channel: r.lead_channel }));
      if (r.message) setDmMessage(r.message);
      setShot((s) => ({ ...s, state: "found", apollo: r.apollo_backfilled }));
    } catch (e) {
      setShot((s) => ({ ...s, state: "miss", reason: "error" }));
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    takeFile(e.dataTransfer.files?.[0]);
  }

  // --- Save

  async function save() {
    setError("");
    if (!form.first_name.trim() && !form.email.trim()) {
      return setError("A name or an email is required.");
    }
    setSaving(true);
    try {
      const { contact } = await api("/contacts", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          kind: "lead",
          first_touch_detail:
            tab === "url" ? "quick-add: LinkedIn URL"
            : tab === "screenshot" ? "quick-add: DM screenshot"
            : "quick-add: manual",
        }),
      });

      // The screenshot's DM becomes a timeline entry, with the image itself
      // stored as an attachment on that activity.
      if (shot?.file) {
        const activity = await api("/activities", {
          method: "POST",
          body: JSON.stringify({
            type: DM_ACTIVITY[form.lead_channel] || "note",
            contact_id: contact.id,
            title: `${CHANNEL_LABELS[form.lead_channel] || "DM"} screenshot`,
            body: dmMessage || null,
          }),
        });
        await apiUpload(`/activities/${activity.id}/attachment?filename=${encodeURIComponent(shot.file.name)}`, shot.file);
      }
      onSaved(contact);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  // --- Shared field block. Every mode saves the same contact, so all three
  // show the same editable fields; only the capture step above them differs.

  const fields = (
    <>
      <div className="crm-field crm-field--row">
        <div className="crm-field">
          <label className="crm-label">First name</label>
          <input className="crm-input" value={form.first_name} onChange={set("first_name")} />
        </div>
        <div className="crm-field">
          <label className="crm-label">Last name</label>
          <input className="crm-input" value={form.last_name} onChange={set("last_name")} />
        </div>
      </div>
      <div className="crm-field">
        <label className="crm-label">Job title</label>
        <input className="crm-input" value={form.job_title} onChange={set("job_title")} />
      </div>
      <div className="crm-field">
        <label className="crm-label">Company</label>
        <input className="crm-input" value={form.company_name} onChange={set("company_name")} />
      </div>
      <div className="crm-field">
        <label className="crm-label">Email (optional)</label>
        <input className="crm-input" type="email" value={form.email} onChange={set("email")} />
      </div>
      <div className="crm-field">
        <label className="crm-label">Channel</label>
        <div className="crm-pillradio">
          {Object.entries(CHANNEL_LABELS).map(([v, l]) => (
            <button
              key={v}
              className={form.lead_channel === v ? "sel" : ""}
              onClick={() => setForm({ ...form, lead_channel: v })}
            >{l}</button>
          ))}
        </div>
      </div>
    </>
  );

  return (
    <div className="crm-modal__backdrop" onClick={onClose}>
      <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal__head">
          <h2>New lead</h2>
        </div>

        <div className="crm-modal__body">
          <div className="crm-seg">
            {TABS.map(([v, l]) => (
              <button key={v} className={tab === v ? "sel" : ""} onClick={() => setTab(v)}>{l}</button>
            ))}
          </div>

          {tab === "url" && (
            <div className="crm-field">
              <label className="crm-label">LinkedIn profile URL</label>
              <input
                className="crm-input"
                autoFocus
                placeholder="linkedin.com/in/…"
                value={form.linkedin_url}
                onChange={set("linkedin_url")}
                onBlur={lookUp}
                onKeyDown={(e) => e.key === "Enter" && lookUp()}
              />
              {lookup.state === "looking" && <span className="crm-hint">Looking up…</span>}
              {lookup.state === "found" && <span className="crm-hint crm-hint--found">Found via Apollo ✓</span>}
              {lookup.state === "miss" && <span className="crm-hint crm-hint--warn">{LOOKUP_MESSAGE[lookup.reason]}</span>}
            </div>
          )}

          {tab === "screenshot" && (
            <div className="crm-field">
              <div
                className={`crm-drop${dragging ? " crm-drop--over" : ""}`}
                onClick={() => fileInput.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
              >
                {shot ? (
                  <img className="crm-drop__thumb" src={shot.url} alt="Dropped screenshot" />
                ) : (
                  <>
                    <b>Drop a WhatsApp or LinkedIn DM screenshot</b>
                    <span>The name, company and message text are read out of the image. Or click to pick a file.</span>
                  </>
                )}
              </div>
              <input
                ref={fileInput}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                style={{ display: "none" }}
                onChange={(e) => takeFile(e.target.files?.[0])}
              />
              {shot?.state === "reading" && <span className="crm-hint">Reading the screenshot…</span>}
              {shot?.state === "found" && (
                <span className="crm-hint crm-hint--found">
                  Read from the screenshot ✓{shot.apollo ? " · email backfilled via Apollo" : ""}
                </span>
              )}
              {shot?.state === "miss" && <span className="crm-hint crm-hint--warn">{EXTRACT_MESSAGE[shot.reason]}</span>}
              <span className="crm-hint" style={{ marginTop: 6 }}>The screenshot is saved to the lead’s timeline.</span>
            </div>
          )}

          {fields}

          {tab === "screenshot" && shot && (
            <div className="crm-field">
              <label className="crm-label">Message</label>
              <input className="crm-input" value={dmMessage} onChange={(e) => setDmMessage(e.target.value)} />
            </div>
          )}

          {error && <p className="crm-login__err" style={{ marginTop: 0 }}>{error}</p>}
        </div>

        <div className="crm-modal__foot">
          <button className="crm-btn crm-btn--dark" disabled={saving} onClick={save}>
            {saving ? "Adding…" : "Add lead"}
          </button>
          <button className="crm-btn crm-btn--ghost" disabled={saving} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
